import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, ChevronRight, RefreshCw, Shuffle, Trash2, Upload, X } from "lucide-react";
import { Fragment, useRef, useState } from "react";
import { Link } from "react-router-dom";

import {
  borrarArbitraje,
  cargarArbitraje,
  getArbitrajeContrapartes,
  getArbitrajeDetalle,
  getArbitrajeResumen,
  getArbitrajeVar,
  previaArbitraje,
  recalcularArbitrajeVar,
  type ArbDia,
  type ArbHora,
  type ArbPrevia,
  type ArbSistema,
} from "@/lib/api";
import { TarjetasVar } from "@/pages/VarDartPage";

const SISTEMAS: ArbSistema[] = ["SIN", "BCA", "BCS"];

const num = (n: number | null | undefined, dec = 3) =>
  n == null ? "—" : n.toLocaleString("es-MX", {
    minimumFractionDigits: dec, maximumFractionDigits: dec,
  });

const fechaCorta = (iso: string) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString("es-MX", {
    weekday: "short", day: "2-digit", month: "short", year: "numeric",
  });

const claveDia = (d: { id_contraparte: number; fecha: string; centro_carga: string }) =>
  `${d.id_contraparte}|${d.fecha}|${d.centro_carga}`;

/*
 * Arbitraje: carga de la demanda fija de UN DÍA desde la plantilla de CENACE
 * "Oferta de Compra de Energía" (OCEvr1), un archivo por día y centro de carga.
 *
 * Primero se elige la contraparte del arbitraje; luego el archivo va a /previa,
 * que solo parsea, y el usuario confirma. El sistema detectado en el Excel
 * (botón de opción BCA/SIN/BCS) solo prellena el combo: la plantilla trae BCA
 * marcado de fábrica, así que lo que se guarda es lo que quede en el combo.
 *
 * Al guardar, el backend corre el VaR DART de /var-dart con el nodo asignado al
 * centro de carga (Configuración → Centros de carga), los parámetros de ese
 * nodo (Configuración → Nodos Cauchy) y la demanda en POSITIVO (compra en MDA),
 * y guarda racionales y resultados; se ven al abrir el día.
 */
export function ArbitrajePage() {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [idContraparte, setIdContraparte] = useState<number | "">("");
  const [archivo, setArchivo] = useState<File | null>(null);
  const [previa, setPrevia] = useState<ArbPrevia | null>(null);
  const [sistema, setSistema] = useState<ArbSistema | "">("");
  const [abierto, setAbierto] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  const qCp = useQuery({ queryKey: ["arbitraje-contrapartes"], queryFn: getArbitrajeContrapartes });
  const q = useQuery({ queryKey: ["arbitraje-resumen"], queryFn: getArbitrajeResumen });
  const dias = q.data?.items ?? [];

  const limpiar = () => {
    setPrevia(null);
    setArchivo(null);
    setSistema("");
    if (fileRef.current) fileRef.current.value = "";
  };

  const verPrevia = useMutation({
    mutationFn: (f: File) => {
      const fd = new FormData();
      fd.append("archivo", f);
      fd.append("id_contraparte", String(idContraparte));
      return previaArbitraje(fd);
    },
    onSuccess: (p) => {
      setError(null);
      setAviso(null);
      setPrevia(p);
      setSistema(p.sistema_archivo ?? "");
    },
    onError: (e) => {
      setPrevia(null);
      setError(e instanceof Error ? e.message : "No se pudo leer el archivo");
    },
  });

  const guardar = useMutation({
    mutationFn: () => {
      const fd = new FormData();
      fd.append("archivo", archivo as File);
      fd.append("id_contraparte", String(previa?.id_contraparte));
      fd.append("sistema", sistema);
      return cargarArbitraje(fd);
    },
    onSuccess: (r) => {
      setError(r.var_error ? `El día se guardó, pero el VaR no se calculó: ${r.var_error}` : null);
      setAviso(
        `${previa?.contraparte} · ${r.centro_carga} · ${r.fecha} (${r.sistema}): ` +
          `${r.guardadas} horas guardadas, ${num(r.mwh)} MWh` +
          (r.reemplazadas ? ` · se reemplazaron ${r.reemplazadas} horas anteriores` : "") +
          (r.var
            ? ` · VaR ${pctDe(r.var.confianza)} %: ${usd(r.var.var_usd)} USD, CVaR ${usd(r.var.cvar_usd)} USD`
            : ""),
      );
      // Abre el día recién guardado para que se vea su VaR.
      if (previa) {
        setAbierto(claveDia({ id_contraparte: previa.id_contraparte, fecha: r.fecha, centro_carga: r.centro_carga }));
      }
      qc.invalidateQueries({ queryKey: ["arbitraje-var"] });
      limpiar();
      qc.invalidateQueries({ queryKey: ["arbitraje-resumen"] });
    },
    onError: (e) => setError(e instanceof Error ? e.message : "No se pudo guardar"),
  });

  const borrar = useMutation({
    mutationFn: (d: ArbDia) => borrarArbitraje(d),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["arbitraje-resumen"] }),
    onError: (e) => setError(e instanceof Error ? e.message : "No se pudo borrar"),
  });

  const onArchivo = (f: File | null) => {
    if (!f) return;
    setArchivo(f);
    verPrevia.mutate(f);
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
          <Shuffle className="w-5 h-5 text-slate-500" /> Arbitraje
        </h1>
        <p className="text-sm text-slate-500 mt-1 max-w-3xl">
          Carga la <b>demanda fija de un día</b> desde la plantilla de CENACE{" "}
          <i>Oferta de Compra de Energía</i> (OCEvr1): <b>un archivo por día y centro de carga</b>.
          Se toman la <b>Fecha inicial</b>, el <b>centro de carga</b>, el <b>sistema</b> y la
          columna <b>Demanda Fija MW</b> por hora; los segmentos de precio no se guardan. Volver a
          subir el mismo día y centro de carga de una contraparte lo <b>reemplaza</b>.
        </p>
      </div>

      <div className="bg-white border rounded-lg p-4 flex flex-wrap items-end gap-3">
        <label className="text-sm">
          <span className="block text-slate-600 mb-1">Contraparte del arbitraje</span>
          <select
            value={idContraparte}
            onChange={(e) => {
              setIdContraparte(e.target.value ? Number(e.target.value) : "");
              limpiar();
            }}
            className="border rounded px-3 py-2 min-w-[20rem] bg-white"
          >
            <option value="">— Elige una contraparte —</option>
            {(qCp.data ?? []).map((c) => (
              <option key={c.id_contraparte} value={c.id_contraparte}>
                {c.nombre}{c.alias ? ` (${c.alias})` : ""}
              </option>
            ))}
          </select>
        </label>
        <input
          ref={fileRef}
          type="file"
          accept=".xlsx,.xlsm"
          className="hidden"
          onChange={(e) => onArchivo(e.target.files?.[0] ?? null)}
        />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={!idContraparte || verPrevia.isPending || guardar.isPending}
          title={idContraparte ? undefined : "Primero elige la contraparte"}
          className="inline-flex items-center gap-2 bg-blue-600 text-white rounded px-4 py-2 text-sm hover:bg-blue-700 disabled:opacity-50"
        >
          <Upload className="w-4 h-4" />
          {verPrevia.isPending ? "Leyendo…" : "Cargar Excel del día"}
        </button>
        {qCp.isError && (
          <span className="text-sm text-red-600">{(qCp.error as Error).message}</span>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded p-3 text-sm">
          {error}
        </div>
      )}
      {aviso && (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 rounded p-3 text-sm">
          {aviso}
        </div>
      )}

      {/* ---------------- Previa ------------------------------------------- */}
      {previa && (
        <div className="border border-blue-200 bg-blue-50/40 rounded-lg">
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-blue-200">
            <div className="text-sm">
              <div className="font-semibold text-slate-900">Previa de «{previa.archivo}»</div>
              <div className="text-slate-600 mt-0.5">
                {previa.contraparte} · {previa.horas.length} horas ·{" "}
                <b>{num(previa.resumen.mwh)} MWh</b>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => guardar.mutate()}
                disabled={guardar.isPending || !sistema}
                title={sistema ? undefined : "Elige el sistema"}
                className="inline-flex items-center gap-2 bg-emerald-600 text-white rounded px-4 py-2 text-sm hover:bg-emerald-700 disabled:opacity-50"
              >
                {guardar.isPending ? "Guardando y simulando VaR…" : "Confirmar y guardar"}
              </button>
              <button
                type="button"
                onClick={limpiar}
                className="inline-flex items-center gap-1 border bg-white text-slate-600 rounded px-3 py-2 text-sm hover:bg-slate-50"
              >
                <X className="w-4 h-4" /> Descartar
              </button>
            </div>
          </div>

          <div className="p-4 space-y-3">
            {previa.ya_cargado && (
              <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded p-3 text-sm">
                Este día y centro de carga <b>ya están cargados</b> para {previa.contraparte}: al
                guardar se reemplazan.
              </div>
            )}
            {previa.avisos.map((a) => (
              <div key={a} className="bg-slate-100 border border-slate-200 text-slate-700 rounded p-3 text-sm">
                {a}
              </div>
            ))}

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Dato titulo="Fecha" valor={fechaCorta(previa.fecha)} />
              <Dato titulo="Centro de carga" valor={previa.centro_carga} />
              <div className="bg-white border rounded px-3 py-2">
                <div className="text-xs text-slate-500">Sistema</div>
                <select
                  value={sistema}
                  onChange={(e) => setSistema(e.target.value as ArbSistema)}
                  className="mt-0.5 border rounded px-2 py-1 text-sm font-semibold bg-white"
                >
                  <option value="">—</option>
                  {SISTEMAS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <div className="text-[11px] text-slate-500 mt-1">
                  {previa.sistema_archivo
                    ? `En el archivo: ${previa.sistema_archivo}` +
                      (sistema && sistema !== previa.sistema_archivo ? " (corregido)" : "")
                    : "No se leyó del archivo"}
                </div>
              </div>
              <Dato
                titulo="Pico / mínimo"
                valor={`${num(previa.resumen.maximo)} MW (h${previa.resumen.hora_pico}) / ${num(previa.resumen.minimo)}`}
              />
            </div>

            <TablaHoras horas={previa.horas} />
          </div>
        </div>
      )}

      {/* ---------------- Lo que ya está cargado --------------------------- */}
      <div>
        <h2 className="text-sm font-semibold text-slate-700 mb-2">Días cargados</h2>
        {q.isLoading && (
          <div className="bg-white border rounded-lg p-8 text-center text-slate-500 text-sm">Cargando…</div>
        )}
        {q.isError && (
          <div className="bg-white border rounded-lg p-8 text-center text-red-600 text-sm">
            {(q.error as Error).message}
          </div>
        )}
        {!q.isLoading && !q.isError && dias.length === 0 && (
          <div className="bg-white border rounded-lg p-8 text-center text-slate-500 text-sm">
            Todavía no hay días cargados: elige la contraparte y sube el Excel del día.
          </div>
        )}
        {dias.length > 0 && (
          <div className="bg-white border rounded-lg overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-100 text-slate-600 text-left">
                <tr>
                  <th className="px-2 py-2 w-6" />
                  <th className="px-3 py-2">Fecha</th>
                  <th className="px-3 py-2">Contraparte</th>
                  <th className="px-3 py-2">Centro de carga</th>
                  <th className="px-3 py-2">Sistema</th>
                  <th className="px-3 py-2 text-right">Horas</th>
                  <th className="px-3 py-2 text-right">MWh</th>
                  <th className="px-3 py-2 text-right">Máx. MW</th>
                  <th className="px-3 py-2 text-right">VaR (USD)</th>
                  <th className="px-3 py-2 text-right">CVaR (USD)</th>
                  <th className="px-3 py-2">Cargado</th>
                  <th className="px-3 py-2 w-8" />
                </tr>
              </thead>
              <tbody>
                {dias.map((d) => {
                  const k = claveDia(d);
                  const ab = abierto === k;
                  return (
                    <Fragment key={k}>
                      <tr className="border-t hover:bg-slate-50 cursor-pointer"
                        onClick={() => setAbierto(ab ? null : k)}>
                        <td className="px-2 py-1.5 text-slate-400">
                          {ab ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                        </td>
                        <td className="px-3 py-1.5 tabular-nums">{fechaCorta(d.fecha)}</td>
                        <td className="px-3 py-1.5">{d.contraparte}</td>
                        <td className="px-3 py-1.5">{d.centro_carga}</td>
                        <td className="px-3 py-1.5">{d.sistema}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums">{d.horas}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums">{num(d.mwh)}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums">{num(d.maximo)}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums"
                          title={d.var_confianza != null ? `Confianza ${pctDe(d.var_confianza)} %` : "Sin VaR"}>
                          {d.var_usd == null ? <span className="text-slate-400">—</span> : usd(d.var_usd)}
                        </td>
                        <td className="px-3 py-1.5 text-right tabular-nums">
                          {d.cvar_usd == null ? <span className="text-slate-400">—</span> : usd(d.cvar_usd)}
                        </td>
                        <td className="px-3 py-1.5 text-xs text-slate-500" title={d.archivo ?? ""}>
                          {new Date(d.cargado_en).toLocaleString("es-MX", {
                            day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
                          })}
                          {d.cargado_por ? ` · ${d.cargado_por}` : ""}
                        </td>
                        <td className="px-2 py-1.5">
                          <button
                            type="button"
                            title="Borrar este día"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (window.confirm(
                                `¿Borrar ${d.centro_carga} del ${d.fecha} (${d.contraparte})?`)) {
                                borrar.mutate(d);
                              }
                            }}
                            className="text-slate-400 hover:text-red-600"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                      {ab && (
                        <tr className="border-t bg-slate-50/60">
                          <td colSpan={12} className="px-4 py-3"><DetalleDia dia={d} /></td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
            <div className="px-3 py-2 border-t text-xs text-slate-500">
              {dias.length} día(s) cargado(s) · {num(dias.reduce((a, d) => a + d.mwh, 0))} MWh en total
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Dato({ titulo, valor }: { titulo: string; valor: string }) {
  return (
    <div className="bg-white border rounded px-3 py-2">
      <div className="text-xs text-slate-500">{titulo}</div>
      <div className="font-semibold text-slate-800 mt-0.5">{valor}</div>
    </div>
  );
}

/** Las horas del día con una barra proporcional al máximo, para ver el perfil. */
function TablaHoras({ horas }: { horas: ArbHora[] }) {
  const max = Math.max(...horas.map((h) => h.demanda_mw), 0) || 1;
  return (
    <div className="bg-white border rounded-lg max-h-96 overflow-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-100 text-slate-600 text-left sticky top-0">
          <tr>
            <th className="px-3 py-1.5 text-right w-16">Hora</th>
            <th className="px-3 py-1.5 text-right w-32">Demanda fija MW</th>
            <th className="px-3 py-1.5" />
          </tr>
        </thead>
        <tbody>
          {horas.map((h, i) => (
            <tr key={h.hora} className={i % 2 ? "bg-slate-50/60" : undefined}>
              <td className="px-3 py-1 text-right tabular-nums">{h.hora}</td>
              <td className="px-3 py-1 text-right tabular-nums">{num(h.demanda_mw)}</td>
              <td className="px-3 py-1">
                <div className="h-2.5 rounded bg-blue-500/70"
                  style={{ width: `${(h.demanda_mw / max) * 100}%` }} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DetalleDia({ dia }: { dia: ArbDia }) {
  const q = useQuery({
    queryKey: ["arbitraje-detalle", claveDia(dia)],
    queryFn: () => getArbitrajeDetalle(dia),
  });
  if (q.isLoading) return <div className="text-sm text-slate-500">Cargando…</div>;
  if (q.isError) return <div className="text-sm text-red-600">{(q.error as Error).message}</div>;
  return (
    <div className="space-y-4">
      <VarDia dia={dia} />
      <div className="space-y-1">
        <div className="text-xs text-slate-500">Archivo: {dia.archivo ?? "—"}</div>
        <TablaHoras horas={q.data?.horas ?? []} />
      </div>
    </div>
  );
}

/**
 * VaR DART guardado del día: las tarjetas de /var-dart y, abajo, las
 * racionales con que se corrió (nodo, fechas, location/scale, LH, TC…).
 */
function VarDia({ dia }: { dia: ArbDia }) {
  const qc = useQueryClient();
  const k = claveDia(dia);
  const q = useQuery({ queryKey: ["arbitraje-var", k], queryFn: () => getArbitrajeVar(dia) });
  const v = q.data?.var ?? null;

  const recalc = useMutation({
    mutationFn: () => recalcularArbitrajeVar(dia),
    onSuccess: (r) => {
      qc.setQueryData(["arbitraje-var", k], { var: r.var });
      qc.invalidateQueries({ queryKey: ["arbitraje-resumen"] });
    },
  });

  if (q.isLoading) return <div className="text-sm text-slate-500">Cargando VaR…</div>;
  if (q.isError) return <div className="text-sm text-red-600">{(q.error as Error).message}</div>;

  const boton = (
    <button
      type="button"
      onClick={() => recalc.mutate()}
      disabled={recalc.isPending}
      title="Vuelve a correr el VaR con el nodo y los parámetros vigentes"
      className="inline-flex items-center gap-1.5 border bg-white text-slate-700 rounded px-3 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-50"
    >
      <RefreshCw className={`w-4 h-4 ${recalc.isPending ? "animate-spin" : ""}`} />
      {recalc.isPending ? "Simulando…" : v ? "Recalcular VaR" : "Calcular VaR"}
    </button>
  );
  const errorRecalc = recalc.isError && (
    <div className="text-sm text-red-600">{(recalc.error as Error).message}</div>
  );

  if (!v) {
    return (
      <div className="bg-white border rounded-lg p-3 space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="text-sm text-slate-600">
            Este día no tiene VaR DART. Se calcula con el nodo asignado en{" "}
            <Link to="/arbitraje/centros-carga" className="text-blue-600 hover:underline">
              Configuración → Centros de carga
            </Link>.
          </div>
          {boton}
        </div>
        {errorRecalc}
      </div>
    );
  }

  const n = (x: number | null) => (x == null ? NaN : x);
  const pct = pctDe(v.confianza);
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm font-semibold text-slate-800">
          VaR DART (Montecarlo) · calculado el{" "}
          {new Date(v.calculado_en).toLocaleString("es-MX", {
            day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
          })}
          {v.calculado_por ? ` · ${v.calculado_por}` : ""}
        </div>
        {boton}
      </div>
      {errorRecalc}

      <TarjetasVar
        confianza={v.confianza}
        v={{
          varD: n(v.var_usd), cvarD: n(v.cvar_usd), pctSup: n(v.pct_sup_usd),
          promSup: n(v.prom_sup_usd), utilEsp: n(v.util_esp_usd), perdEsp: n(v.perd_esp_usd),
          media: n(v.media_usd), p50: n(v.p50_usd), volumen: v.volumen_mwh, tc: v.tc,
        }}
      />

      <div className="bg-white border rounded-lg p-3">
        <div className="text-xs font-semibold text-slate-600 mb-2">Racionales</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-2 text-sm">
          <Racional titulo="Nodo / zona"
            valor={`${v.punto_etiqueta ?? v.punto_clave} · ${v.punto_tipo === "zona" ? "Zona (PND)" : "Nodo (PML)"} · ${v.punto_clave}`} />
          <Racional titulo="Fecha de cálculo" valor={fechaDMA(v.fecha_calculo)}
            nota={`ventana de ${v.ventana_dias} días · operación futura: último disponible; si no, ≤ operación − 5 d`} />
          <Racional titulo="Fecha de operación" valor={fechaDMA(v.fecha)} />
          <Racional titulo="Confianza" valor={`${pct} %`} />
          <Racional titulo="Location (MXN/MWh)" valor={num(v.location, 2)} />
          <Racional titulo="Scale (MXN/MWh)" valor={num(v.scale, 2)} />
          <Racional titulo="LH Mín DART (MXN/MWh)" valor={num(v.lh_min, 0)} />
          <Racional titulo="LH Máx DART (MXN/MWh)" valor={num(v.lh_max, 0)} />
          <Racional titulo="Tipo de cambio (MXN/USD)" valor={num(v.tc, 4)} />
          <Racional titulo="Trading Σ |MWh|" valor={num(v.volumen_mwh, 2)}
            nota="demanda en positivo (compra en MDA)" />
          <Racional titulo="Simulaciones" valor={v.n_sim.toLocaleString("es-MX")} />
          <Racional titulo="Semilla" valor={String(v.semilla)} nota="reproduce la corrida" />
        </div>
        <p className="text-[11px] text-slate-500 mt-3">
          DART_h = MAX(LH mín, MIN(LH máx, location + scale·TAN(π·(U − 0.5)))) · PnL = Σ DART_h · −MWh_h ÷ TC ·
          VaR = percentil {num(100 - pct, 1)} % del PnL · CVaR = promedio de los PnL menores que el VaR.
          Location = mediana y scale = mitad del rango intercuartil de la Cauchy ajustada al DART
          (MDA − MTR) de los {v.ventana_dias} días que terminan en la fecha de cálculo.
        </p>
      </div>
    </div>
  );
}

function Racional({ titulo, valor, nota }: { titulo: string; valor: string; nota?: string }) {
  return (
    <div>
      <div className="text-[11px] text-slate-500">{titulo}</div>
      <div className="font-medium text-slate-800 tabular-nums">{valor}</div>
      {nota && <div className="text-[11px] text-slate-400">{nota}</div>}
    </div>
  );
}

function usd(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return "—";
  return `${v < 0 ? "−" : ""}$${Math.abs(v).toLocaleString("es-MX", {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`;
}

const pctDe = (c: number) => Math.round(c * 1000) / 10;

const fechaDMA = (iso: string) => {
  const [a, m, d] = iso.slice(0, 10).split("-");
  return `${d}/${m}/${a}`;
};
